/**
 * iter132 — Crew workflow buttons for the Route page job sheet.
 *
 * Renders 4 sequential action buttons that walk a crew through a job:
 *   1. "I'm On My Way"    → prompts for ETA (auto-computed via Google
 *                            Distance Matrix + manual override), sends
 *                            customer push.
 *   2. "I've Arrived"     → sends customer push.
 *   3. "Service Started"  → sets status=in_progress, sends customer push.
 *   4. "Service Completed"→ requires ≥1 Before + ≥1 After photo, sets
 *                            status=completed, sends customer push (if
 *                            unpaid the push deep-links to Pay Now).
 *
 * All buttons post to endpoints added in iter132:
 *   POST /api/admin/bookings/{id}/on-my-way        {eta_minutes}
 *   POST /api/admin/bookings/{id}/arrived
 *   POST /api/admin/bookings/{id}/service-started
 *   POST /api/admin/bookings/{id}/service-completed
 *
 * Photo upload uses the existing /api/admin/bookings/{id}/photos endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/config";
import { Button } from "./ui/button";
import { Truck, MapPin, Play, CheckCircle2, Loader2, Camera, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/** Compute driving ETA from user's current geolocation to (lat, lng)
 *  using the Google Distance Matrix service. Returns minutes rounded up
 *  or null if unavailable. Assumes google.maps.* is already loaded (the
 *  Route Map panel always loads it before this component mounts). */
async function computeEtaMinutes(destLat, destLng) {
  try {
    if (typeof window === "undefined" || !window.google?.maps?.DistanceMatrixService) return null;
    if (!destLat || !destLng) return null;
    // Ask browser for current position
    const origin = await new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
      );
    });
    if (!origin) return null;
    const svc = new window.google.maps.DistanceMatrixService();
    return await new Promise((resolve) => {
      svc.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [{ lat: destLat, lng: destLng }],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (res, status) => {
          if (status !== "OK") return resolve(null);
          const el = res?.rows?.[0]?.elements?.[0];
          if (!el || el.status !== "OK" || !el.duration?.value) return resolve(null);
          resolve(Math.max(1, Math.round(el.duration.value / 60)));
        }
      );
    });
  } catch {
    return null;
  }
}

function Thumb({ src }) {
  return (
    <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" /> : null}
    </div>
  );
}

// iter183 — Photos may be either legacy base64 `data` or new media_id.
function photoSrc(p) {
  if (p?.media_id) return `${API}/media/${p.media_id}`;
  if (p?.url && typeof p.url === "string" && p.url.startsWith("/api/")) {
    return `${API.replace(/\/api$/, "")}${p.url}`;
  }
  return p?.data || "";
}

function PhotoRow({ label, kind, list, uploading, onFile }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1.5">
        {label} <span className="text-gray-400">({list.length})</span>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        {list.map((p) => <Thumb key={p.photo_id} src={photoSrc(p)} />)}
        <label
          className={`w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 grid place-items-center cursor-pointer hover:border-[#047857] hover:bg-emerald-50/40 ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          data-testid={`complete-add-${kind}-photo`}
        >
          <Camera className="w-5 h-5 text-gray-500" />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onFile(kind, f); }}
          />
        </label>
      </div>
    </div>
  );
}

function CrewActionButton({ label, icon: Icon, onClick, testid, active, disabled, isBusy }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className={`h-11 rounded-full font-extrabold text-sm ${active
        ? "bg-[#047857] hover:bg-[#065F46] text-white shadow-lg shadow-emerald-900/20"
        : "bg-white text-[#047857] border border-emerald-200 hover:bg-emerald-50"} disabled:opacity-40`}
      data-testid={testid}
    >
      {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 mr-1.5" />}
      {label}
    </Button>
  );
}

function OnMyWayDialog({ open, job, onClose, onSent }) {
  const [autoEta, setAutoEta] = useState(null);       // null = loading
  const [etaInput, setEtaInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAutoEta(null);
    setEtaInput("");
    let cancelled = false;
    (async () => {
      const min = await computeEtaMinutes(job?.lat, job?.lng);
      if (cancelled) return;
      setAutoEta(min);
      if (min != null) setEtaInput(String(min));
    })();
    return () => { cancelled = true; };
  }, [open, job?.lat, job?.lng]);

  const send = async () => {
    const parsed = etaInput.trim() === "" ? null : parseInt(etaInput, 10);
    if (parsed != null && (isNaN(parsed) || parsed < 0 || parsed > 600)) {
      return toast.error("ETA must be between 0 and 600 minutes");
    }
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${job.booking_id}/on-my-way`,
        { eta_minutes: parsed });
      toast.success(parsed != null
        ? `Customer notified — ETA ${parsed} min`
        : "Customer notified — you're on the way");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send notification");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="on-my-way-dialog">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#047857]" />
            <div className="font-extrabold tracking-tight font-[Outfit]">I&apos;m on my way</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm text-gray-700">
            Notify <b>{job.name || "the customer"}</b> that you&apos;re heading over. Their notification will
            include your ETA.
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">
              ETA (minutes)
            </label>
            <input
              type="number"
              min="0"
              max="600"
              value={etaInput}
              onChange={(e) => setEtaInput(e.target.value)}
              placeholder={autoEta != null ? String(autoEta) : "e.g. 15"}
              className="w-full h-11 rounded-xl border border-gray-300 px-3 text-lg font-bold tracking-tight focus:outline-none focus:ring-2 focus:ring-[#047857]"
              data-testid="on-my-way-eta-input"
            />
            <div className="mt-1.5 text-[11px] text-gray-500">
              {autoEta === null
                ? "Auto-calculating from your current location…"
                : autoEta != null
                  ? <>Google Maps suggested <b>{autoEta} min</b>. Edit to override.</>
                  : "Could not auto-compute. Type in an ETA manually or leave blank."}
            </div>
          </div>
          <Button
            onClick={send}
            disabled={busy}
            className="w-full h-11 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
            data-testid="on-my-way-send-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send notification"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompleteJobDialog({ open, job, onClose, onSent }) {
  const [before, setBefore] = useState(job?.before_photos || []);
  const [after, setAfter] = useState(job?.after_photos || []);
  const [uploading, setUploading] = useState(false);
  const [completing, setCompleting] = useState(false);

  const uploadPhoto = async (kind, file) => {
    if (!file) return;
    setUploading(true);
    try {
      // iter183: upload to /api/media/upload first, then attach media_id.
      // No hard 5MB limit — server compresses + stores original + optimized.
      const form = new FormData();
      form.append("file", file, file.name || `photo-${kind}.jpg`);
      let desc, lastErr;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data } = await api.post("/media/upload", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          desc = data; break;
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500) throw e;
          if (attempt < 3) await new Promise((r) => setTimeout(r, 600 * attempt));
        }
      }
      if (!desc) throw lastErr;
      const { data: res } = await api.post(`/admin/bookings/${job.booking_id}/photos`, {
        kind, media_id: desc.media_id, caption: "",
      });
      const entry = {
        photo_id: res.photo_id, media_id: desc.media_id, kind,
        url: `/api/media/${desc.media_id}`,
        uploaded_at: new Date().toISOString(),
      };
      if (kind === "before") setBefore((p) => [...p, entry]);
      else setAfter((p) => [...p, entry]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const complete = async () => {
    if (!before.length || !after.length) {
      return toast.error("At least one Before and one After photo required");
    }
    setCompleting(true);
    try {
      await api.post(`/admin/bookings/${job.booking_id}/service-completed`);
      toast.success("Job marked complete — customer notified");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete the job");
    } finally {
      setCompleting(false);
    }
  };

  if (!open) return null;

  const canComplete = before.length > 0 && after.length > 0 && !completing;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="complete-job-dialog">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#047857]" />
            <div className="font-extrabold tracking-tight font-[Outfit]">Complete job</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="text-sm text-gray-700">
            Add at least one <b>Before</b> and one <b>After</b> photo. The customer will see these in
            their job history.
          </div>
          <PhotoRow label="Before" kind="before" list={before} uploading={uploading} onFile={uploadPhoto} />
          <PhotoRow label="After" kind="after" list={after} uploading={uploading} onFile={uploadPhoto} />
          <Button
            onClick={complete}
            disabled={!canComplete}
            className="w-full h-11 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold disabled:opacity-50"
            data-testid="complete-job-send-btn"
          >
            {completing
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark complete &amp; notify customer</>}
          </Button>
          <div className="text-[11px] text-gray-500 leading-snug">
            When you tap complete, the customer gets a push notification and, if the invoice is
            unpaid, it deep-links straight to the Pay Now screen.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CrewWorkflowButtons({ job, onUpdated }) {
  const [onMyWayOpen, setOnMyWayOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [busy, setBusy] = useState(null); // string of the in-flight action

  const status = (job.status || "").toLowerCase();
  const done = status === "completed" || status === "cancelled" || status === "canceled";

  const post = async (action, path) => {
    setBusy(action);
    try {
      await api.post(path);
      const nice = {
        arrived: "Customer notified — you've arrived",
        started: "Customer notified — service started",
      }[action] || "Notification sent";
      toast.success(nice);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send notification");
    } finally {
      setBusy(null);
    }
  };

  const commonDisabled = done || busy != null;

  if (done) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-1.5 justify-end">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
        Job {status}
      </div>
    );
  }

  // Recommend next step based on status
  const nextIs = (() => {
    if (status === "arrived") return "started";
    if (status === "in_progress") return "complete";
    if (status === "on_the_way") return "arrived";
    return "on_my_way";
  })();

  return (
    <>
      <div className="grid grid-cols-2 gap-2 w-full" data-testid="crew-workflow-buttons-wrap">
        <CrewActionButton
          label="On my way" icon={Truck}
          onClick={() => setOnMyWayOpen(true)}
          testid="crew-on-my-way-btn"
          active={nextIs === "on_my_way"}
          disabled={commonDisabled}
          isBusy={busy === "crew-on-my-way-btn"}
        />
        <CrewActionButton
          label="Arrived" icon={MapPin}
          onClick={() => post("arrived", `/admin/bookings/${job.booking_id}/arrived`)}
          testid="crew-arrived-btn"
          active={nextIs === "arrived"}
          disabled={commonDisabled}
          isBusy={busy === "arrived"}
        />
        <CrewActionButton
          label="Started" icon={Play}
          onClick={() => post("started", `/admin/bookings/${job.booking_id}/service-started`)}
          testid="crew-started-btn"
          active={nextIs === "started"}
          disabled={commonDisabled}
          isBusy={busy === "started"}
        />
        <CrewActionButton
          label="Complete" icon={CheckCircle2}
          onClick={() => setCompleteOpen(true)}
          testid="crew-complete-btn"
          active={nextIs === "complete"}
          disabled={commonDisabled}
          isBusy={busy === "crew-complete-btn"}
        />
      </div>
      <OnMyWayDialog
        open={onMyWayOpen}
        job={job}
        onClose={() => setOnMyWayOpen(false)}
        onSent={onUpdated}
      />
      <CompleteJobDialog
        open={completeOpen}
        job={job}
        onClose={() => setCompleteOpen(false)}
        onSent={onUpdated}
      />
    </>
  );
}
